import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from 'src/app/core/models/api-response';
import { getDepartmentId, getTenantId } from 'src/app/services/store-snapshot.service';
import {
  MainTableFilterModel,
  PrintReportFilterModel,
} from 'src/app/shared/models/main-table-filter.model';
import { createFilterParams } from 'src/app/shared/functions/create-filter-params';
import { MainTableFilteringHelperService } from 'src/app/shared/main-table-module/services/main-table-filtering-helper.service';
import { environment } from 'src/environments/environment';
import { IdName } from '../../shared/models/id-name';
import { IRunTimeImportRequest, IRunTimeImportResult } from '../models/run-time-import.model';
import {
  IRunTimeRequest,
  IRunTime,
  IRunTimeCounterUpdateRequest,
  IRunTimeRecordLogs,
  IRunTimeMinimal,
} from '../models/run-time.model';

const headers = { 'content-type': 'application/json' };
const COLUMN_TO_KEY_MAP: Record<string, string> = {
  responsiblePositions: 'ResponsibleForPositions.Name',
  responsiblePositionGroups: 'ResponsibleForPositionGroups.Name',
  responsibleRoles: 'ResponsibleForRoles.Name',
};
const OR_GROUP_MAP: Record<string, string> = {
  responsiblePositions: 'responsible',
  responsiblePositionGroups: 'responsible',
  responsibleRoles: 'responsible',
};

@Injectable({
  providedIn: 'root',
})
export class RunTimeApiService {
  private get url(): string {
    return `${environment.apiUrl}/api/${getTenantId()}/${getDepartmentId()}/Runtimes`;
  }
  private headers = { 'content-type': 'application/json' };

  constructor(private http: HttpClient) {}

  loadRunTimesForDepartment(
    currentFilter?: MainTableFilterModel
  ): Observable<ApiResponse<IRunTime[]>> {
    const httpOptions = {
      headers: this.headers,
      params: createFilterParams(currentFilter),
    };
    const url = `${this.url}/Department`;

    if (this.shouldIncludeChildren(currentFilter)) {
      httpOptions.params = httpOptions.params.set('IncludeChildren', 'true');
    }
    httpOptions.params = MainTableFilteringHelperService.appendFilteringParams(
      httpOptions.params,
      currentFilter,
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );
    return this.http.get<ApiResponse<IRunTime[]>>(url, httpOptions);
  }

  loadRunTimeChildrenForTable(
    id: string,
    currentFilter?: MainTableFilterModel,
    orderBy?: string,
    ascDesc?: string
  ): Observable<ApiResponse<IRunTime[]>> {
    const httpOptions = {
      headers: this.headers,
      params: createFilterParams(currentFilter),
    };
    const url = `${this.url}/${id}/Children`;

    if (orderBy && ascDesc) {
      httpOptions.params = httpOptions.params.set(`OrderBy[${orderBy}]`, ascDesc);
    }
    httpOptions.params = MainTableFilteringHelperService.appendFilteringParams(
      httpOptions.params,
      currentFilter,
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );
    return this.http.get<ApiResponse<IRunTime[]>>(url, httpOptions);
  }

  loadRunTimesForDepartmentMinimal(
    currentFilter?: MainTableFilterModel
  ): Observable<ApiResponse<IRunTimeMinimal[]>> {
    const httpOptions = {
      headers: this.headers,
      params: createFilterParams(currentFilter),
    };
    const url = `${this.url}/Department/List/v2`;

    return this.http.get<ApiResponse<IRunTimeMinimal[]>>(url, httpOptions);
  }

  loadAllRunTimesForDepartment(includeChildren?: boolean): Observable<IdName[]> {
    const url = `${this.url}/Department/List`;
    if (includeChildren !== undefined) {
      return this.http.request<IdName[]>('GET', url, { params: { includeChildren } });
    }
    return this.http.get<IdName[]>(url);
  }

  loadRunTimeById(id: string): Observable<IRunTime> {
    const url = `${this.url}/${id}`;
    return this.http.get<IRunTime>(url);
  }

  createRunTime(runTime: IRunTimeRequest): Observable<IRunTime> {
    return this.http.post<IRunTime>(this.url, runTime, { headers });
  }

  updateRunTime(runTime: IRunTimeRequest): Observable<IRunTime> {
    const url = `${this.url}/${runTime.id}`;
    return this.http.put<IRunTime>(url, runTime, { headers });
  }

  changeRunTimeParent(id: string, parentRuntimeId?: string): Observable<void> {
    const url = `${this.url}/${id}/ChangeParent`;
    const body = {
      parentRuntimeId,
    };
    return this.http.patch<void>(url, body, { headers });
  }

  updateRunTimeCounter(runTime: IRunTimeCounterUpdateRequest): Observable<IRunTime> {
    const url = `${this.url}/${runTime.id}/Counter`;
    return this.http.put<IRunTime>(url, runTime, { headers });
  }

  deleteRunTime(id: string): Observable<void> {
    const url = `${this.url}/${id}`;
    return this.http.delete<void>(url);
  }

  changeOffset(runTime: IRunTimeCounterUpdateRequest): Observable<string> {
    const url = `${this.url}/${runTime.id}/Offset`;
    return this.http.put<string>(url, runTime);
  }

  loadLog(id: string): Observable<IRunTimeRecordLogs> {
    const url = `${this.url}/${id}/Log`;
    return this.http.get<IRunTimeRecordLogs>(url);
  }

  printLog(id: string): Observable<Blob> {
    const url = `${this.url}/${id}/Log/Report`;
    return this.http.get(url, { responseType: 'blob' });
  }

  printReport(
    filter?: PrintReportFilterModel,
    menuId?: string,
    includeChildren?: boolean
  ): Observable<Blob> {
    const url = `${this.url}/Report`;
    let params = createFilterParams(filter);
    if (menuId) {
      params = params.set('MenuId', menuId);
    }
    if (includeChildren !== undefined) {
      params = params.set('IncludeChildren', includeChildren);
    }
    params = MainTableFilteringHelperService.appendFilteringParams(
      params,
      filter,
      COLUMN_TO_KEY_MAP,
      OR_GROUP_MAP
    );
    return this.http.get(url, { params, responseType: 'blob' });
  }

  importRunTimes(runTimeImport: IRunTimeImportRequest): Observable<IRunTimeImportResult> {
    const url = `${this.url}/Import`;
    const formData = new FormData();

    formData.append('file', runTimeImport.file);

    if (runTimeImport.comment?.trim()) {
      formData.append('comment', runTimeImport.comment.trim());
    }

    return this.http.post<IRunTimeImportResult>(url, formData);
  }

  private shouldIncludeChildren(filter?: MainTableFilterModel): boolean {
    return !!filter?.searchTerm || !!filter?.searchBy?.length;
  }
}
